import { Trash2 } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "../../api/client.js";
import { feedHealthStore } from "../../api/health-store.js";
import {
  useHealthPolicy,
  usePlaylists,
  useSchedules,
} from "../../api/hooks.js";
import { patchFeed, type FeedPatchInput } from "../../api/feeds.js";
import { patchHealthPolicy, type HealthPolicyInput } from "../../api/health.js";
import { nowPlayingStore } from "../../api/now-playing-store.js";
import { Button } from "../../design-system/button.js";
import {
  Field,
  Fieldset,
  FormActions,
  NumberInput,
  Select,
  Switch,
  TextInput,
  Textarea,
} from "../../design-system/form.js";
import { InlineConfirm } from "../../design-system/inline-confirm.js";
import { Notice, Panel } from "../../design-system/surfaces.js";
import { useFeedContext } from "./feed-detail-layout.js";
import { useFeedActions } from "./use-feed-actions.js";
import type { Feed, HealthPolicy } from "../../api/types.js";

/**
 * Everything about this feed that is configuration rather than state: what it is
 * called, which global objects it draws on, how it sounds, and the tolerances it is
 * judged against. Each section saves the fields it owns and nothing else, so a
 * fade the operator did not touch is never re-sent and never re-validated.
 */
export function FeedSettingsTab() {
  const { feed, reload } = useFeedContext();

  // A form field wider than a long sentence is harder to read, not easier to fill,
  // so the whole of this face is measured rather than stretched to the viewport.
  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <FeedForm feed={feed} onSaved={reload} />
      <PolicyForm feedId={feed.id} />
      <DangerZone feed={feed} />
    </div>
  );
}

interface FeedDraft {
  name: string;
  description: string;
  scheduleId: string;
  defaultPlaylistId: string;
  fallbackPlaylistId: string;
  crossfadeMs: string;
  loudnessTargetLufs: string;
  truePeakCeilingDbtp: string;
  loudnessRangeLu: string;
}

function draftOf(feed: Feed): FeedDraft {
  return {
    name: feed.name,
    description: feed.description,
    scheduleId: feed.scheduleId ?? "",
    defaultPlaylistId: feed.defaultPlaylistId,
    fallbackPlaylistId: feed.fallbackPlaylistId ?? "",
    crossfadeMs: String(feed.crossfadeMs),
    loudnessTargetLufs: String(feed.loudnessTargetLufs),
    truePeakCeilingDbtp: String(feed.truePeakCeilingDbtp),
    loudnessRangeLu: String(feed.loudnessRangeLu),
  };
}

function FeedForm({ feed, onSaved }: { feed: Feed; onSaved: () => void }) {
  const playlists = usePlaylists(true);
  const schedules = useSchedules(true);
  const [draft, setDraft] = useState<FeedDraft>(() => draftOf(feed));
  const [saved, setSaved] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDraft(draftOf(feed));
  }, [feed]);

  const original = draftOf(feed);
  const dirty = (Object.keys(original) as (keyof FeedDraft)[]).some(
    (key) => original[key] !== draft[key],
  );
  const available = (playlists.data ?? []).filter(
    (playlist) => playlist.enabled,
  );

  function set<K extends keyof FeedDraft>(key: K, value: FeedDraft[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
    setSaved(null);
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || !dirty) return;
    const input: FeedPatchInput = {};
    if (draft.name.trim() !== original.name) input.name = draft.name.trim();
    if (draft.description !== original.description) {
      input.description = draft.description;
    }
    if (draft.scheduleId !== original.scheduleId) {
      input.schedule_id = draft.scheduleId === "" ? null : draft.scheduleId;
    }
    if (draft.defaultPlaylistId !== original.defaultPlaylistId) {
      input.default_playlist_id = draft.defaultPlaylistId;
    }
    if (draft.fallbackPlaylistId !== original.fallbackPlaylistId) {
      input.fallback_playlist_id =
        draft.fallbackPlaylistId === "" ? null : draft.fallbackPlaylistId;
    }
    if (draft.crossfadeMs !== original.crossfadeMs) {
      input.crossfade_ms = Math.round(Number(draft.crossfadeMs));
    }
    if (draft.loudnessTargetLufs !== original.loudnessTargetLufs) {
      input.loudness_target_lufs = Number(draft.loudnessTargetLufs);
    }
    if (draft.truePeakCeilingDbtp !== original.truePeakCeilingDbtp) {
      input.true_peak_ceiling_dbtp = Number(draft.truePeakCeilingDbtp);
    }
    if (draft.loudnessRangeLu !== original.loudnessRangeLu) {
      input.loudness_range_lu = Number(draft.loudnessRangeLu);
    }

    setBusy(true);
    setError(null);
    setWarning(null);
    setSaved(null);
    patchFeed(feed.id, input)
      .then(() => {
        setSaved("Saved.");
        nowPlayingStore.refresh(feed.id);
        onSaved();
      })
      .catch((cause: unknown) => {
        // The crossfade is already on the row and only this feed's queue has yet to
        // be rewritten with it. Reporting that as a failure would be false, and the
        // operator would try again against a value that is already saved.
        if (
          cause instanceof ApiError &&
          cause.code === "crossfade_reconcile_pending"
        ) {
          setWarning(
            cause.detail || "Saved. The new fade takes effect shortly.",
          );
          onSaved();
          return;
        }
        setError(
          cause instanceof Error ? cause.message : "The feed was not saved.",
        );
      })
      .finally(() => {
        setBusy(false);
      });
  }

  return (
    <Panel className="p-5">
      <form className="flex flex-col gap-6" noValidate onSubmit={submit}>
        <Fieldset legend="Identity">
          <Field htmlFor="settings-name" label="Name">
            <TextInput
              id="settings-name"
              onChange={(event) => {
                set("name", event.target.value);
              }}
              required
              value={draft.name}
            />
          </Field>
          <Field
            className="sm:col-span-2"
            htmlFor="settings-description"
            label="Description"
          >
            <Textarea
              id="settings-description"
              onChange={(event) => {
                set("description", event.target.value);
              }}
              value={draft.description}
            />
          </Field>
        </Fieldset>

        <Fieldset
          description="Playlists and schedules can be shared between feeds."
          legend="Programming"
        >
          <Field htmlFor="settings-default-playlist" label="Default playlist">
            <Select
              id="settings-default-playlist"
              onChange={(event) => {
                set("defaultPlaylistId", event.target.value);
              }}
              value={draft.defaultPlaylistId}
            >
              {available.every(
                (playlist) => playlist.id !== draft.defaultPlaylistId,
              ) ? (
                <option value={draft.defaultPlaylistId}>
                  {playlists.loading ? "…" : "Current playlist"}
                </option>
              ) : null}
              {available.map((playlist) => (
                <option key={playlist.id} value={playlist.id}>
                  {playlist.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field htmlFor="settings-fallback-playlist" label="Fallback playlist">
            <Select
              id="settings-fallback-playlist"
              onChange={(event) => {
                set("fallbackPlaylistId", event.target.value);
              }}
              value={draft.fallbackPlaylistId}
            >
              <option value="">None</option>
              {available.map((playlist) => (
                <option key={playlist.id} value={playlist.id}>
                  {playlist.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field htmlFor="settings-schedule" label="Schedule">
            <Select
              id="settings-schedule"
              onChange={(event) => {
                set("scheduleId", event.target.value);
              }}
              value={draft.scheduleId}
            >
              <option value="">None</option>
              {(schedules.data ?? [])
                .filter((schedule) => schedule.enabled)
                .map((schedule) => (
                  <option key={schedule.id} value={schedule.id}>
                    {schedule.name}
                  </option>
                ))}
            </Select>
          </Field>

          <Field label="Rotation">
            <p className="font-mono text-xs text-ink-3">
              {`seed ${feed.rotationSeed} · counter ${feed.rotationCounter}`}
            </p>
          </Field>
        </Fieldset>

        <Fieldset description="Crossfade and loudness." legend="Audio">
          <Field
            hint="Must be shorter than the shortest track this feed plays."
            htmlFor="settings-crossfade"
            label="Crossfade"
          >
            <NumberInput
              id="settings-crossfade"
              min={0}
              onChange={(event) => {
                set("crossfadeMs", event.target.value);
              }}
              step={100}
              unit="ms"
              value={draft.crossfadeMs}
            />
          </Field>
          <Field htmlFor="settings-loudness" label="Loudness target">
            <NumberInput
              id="settings-loudness"
              max={-5}
              min={-70}
              onChange={(event) => {
                set("loudnessTargetLufs", event.target.value);
              }}
              step={0.1}
              unit="LUFS"
              value={draft.loudnessTargetLufs}
            />
          </Field>
          <Field htmlFor="settings-true-peak" label="True-peak ceiling">
            <NumberInput
              id="settings-true-peak"
              max={0}
              min={-9}
              onChange={(event) => {
                set("truePeakCeilingDbtp", event.target.value);
              }}
              step={0.1}
              unit="dBTP"
              value={draft.truePeakCeilingDbtp}
            />
          </Field>
          <Field htmlFor="settings-loudness-range" label="Loudness range">
            <NumberInput
              id="settings-loudness-range"
              max={50}
              min={1}
              onChange={(event) => {
                set("loudnessRangeLu", event.target.value);
              }}
              step={0.1}
              unit="LU"
              value={draft.loudnessRangeLu}
            />
          </Field>
        </Fieldset>

        {error ? <Notice tone="danger">{error}</Notice> : null}
        {warning ? <Notice tone="warning">{warning}</Notice> : null}

        <FormActions note={saved ?? undefined}>
          <Button disabled={!dirty || busy} type="submit">
            {busy ? "Saving…" : "Save changes"}
          </Button>
          {dirty ? (
            <Button
              onClick={() => {
                setDraft(draftOf(feed));
                setError(null);
                setWarning(null);
              }}
              variant="ghost"
            >
              Discard
            </Button>
          ) : null}
        </FormActions>
      </form>
    </Panel>
  );
}

type PolicyDraft = Record<PolicyKey, string>;

type PolicyKey =
  | "checkIntervalSeconds"
  | "sampleSeconds"
  | "connectTimeoutMs"
  | "silenceThresholdDbfs"
  | "silenceToleranceSeconds"
  | "fallbackToleranceSeconds"
  | "loudnessToleranceLu"
  | "truePeakToleranceDb"
  | "consecutiveFailuresToAlert";

interface PolicyField {
  key: PolicyKey;
  wire: keyof HealthPolicyInput;
  label: string;
  unit: string;
  min: number;
  max: number;
  step: number;
  hint?: string;
}

/**
 * Every tolerance is this feed's own; not one of them is global. The bounds are the
 * published patch schema's, so a value the API would refuse is refused before it is
 * sent — and a value it would accept is never second-guessed here.
 */
const POLICY_FIELDS: PolicyField[] = [
  {
    key: "checkIntervalSeconds",
    wire: "check_interval_seconds",
    label: "Check every",
    unit: "s",
    min: 5,
    max: 3_600,
    step: 1,
    hint: "How often the stream is checked.",
  },
  {
    key: "sampleSeconds",
    wire: "sample_seconds",
    label: "Sample for",
    unit: "s",
    min: 2,
    max: 120,
    step: 1,
  },
  {
    key: "connectTimeoutMs",
    wire: "connect_timeout_ms",
    label: "Connect timeout",
    unit: "ms",
    min: 500,
    max: 60_000,
    step: 500,
  },
  {
    key: "silenceThresholdDbfs",
    wire: "silence_threshold_dbfs",
    label: "Silence below",
    unit: "dBFS",
    min: -120,
    max: 0,
    step: 0.5,
  },
  {
    key: "silenceToleranceSeconds",
    wire: "silence_tolerance_seconds",
    label: "Silence tolerated",
    unit: "s",
    min: 1,
    max: 120,
    step: 1,
    hint: "Cannot exceed the sample: silence longer than the sample could never be observed.",
  },
  {
    key: "fallbackToleranceSeconds",
    wire: "fallback_tolerance_seconds",
    label: "Floor tolerated",
    unit: "s",
    min: 0,
    max: 86_400,
    step: 10,
  },
  {
    key: "loudnessToleranceLu",
    wire: "loudness_tolerance_lu",
    label: "Loudness drift",
    unit: "LU",
    min: 0.1,
    max: 20,
    step: 0.1,
  },
  {
    key: "truePeakToleranceDb",
    wire: "true_peak_tolerance_db",
    label: "True-peak drift",
    unit: "dB",
    min: 0,
    max: 10,
    step: 0.1,
  },
  {
    key: "consecutiveFailuresToAlert",
    wire: "consecutive_failures_to_alert",
    label: "Failing checks before alerting",
    unit: "",
    min: 1,
    max: 100,
    step: 1,
  },
];

function policyDraftOf(policy: HealthPolicy): PolicyDraft {
  return {
    checkIntervalSeconds: String(policy.checkIntervalSeconds),
    sampleSeconds: String(policy.sampleSeconds),
    connectTimeoutMs: String(policy.connectTimeoutMs),
    silenceThresholdDbfs: String(policy.silenceThresholdDbfs),
    silenceToleranceSeconds: String(policy.silenceToleranceSeconds),
    fallbackToleranceSeconds: String(policy.fallbackToleranceSeconds),
    loudnessToleranceLu: String(policy.loudnessToleranceLu),
    truePeakToleranceDb: String(policy.truePeakToleranceDb),
    consecutiveFailuresToAlert: String(policy.consecutiveFailuresToAlert),
  };
}

function PolicyForm({ feedId }: { feedId: string }) {
  const policy = useHealthPolicy(feedId, true);
  const [draft, setDraft] = useState<PolicyDraft | null>(null);
  const [watching, setWatching] = useState(true);
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (policy.data) {
      setDraft(policyDraftOf(policy.data));
      setWatching(policy.data.enabled);
    }
  }, [policy.data]);

  if (!policy.data || !draft) {
    return (
      <Panel className="p-5">
        <h2 className="text-sm font-semibold text-ink">Self-check</h2>
        <p className="mt-2 text-sm text-ink-3">
          {policy.error ?? "Reading this feed's tolerances…"}
        </p>
      </Panel>
    );
  }

  const current = policy.data;
  const original = policyDraftOf(current);
  const dirty =
    watching !== current.enabled ||
    POLICY_FIELDS.some((field) => original[field.key] !== draft[field.key]);
  const silenceTooLong =
    Number(draft.silenceToleranceSeconds) > Number(draft.sampleSeconds);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || !dirty || silenceTooLong || !draft) return;
    const input: HealthPolicyInput = {};
    if (watching !== current.enabled) input.enabled = watching;
    for (const field of POLICY_FIELDS) {
      if (original[field.key] === draft[field.key]) continue;
      const value = Number(draft[field.key]);
      if (!Number.isFinite(value)) continue;
      input[field.wire] = value as never;
    }
    setBusy(true);
    setError(null);
    setSaved(null);
    patchHealthPolicy(feedId, input)
      .then(() => {
        setSaved("Saved.");
        policy.reload();
        feedHealthStore.refresh(feedId);
      })
      .catch((cause: unknown) => {
        setError(
          cause instanceof Error
            ? cause.message
            : "The tolerances were not saved.",
        );
      })
      .finally(() => {
        setBusy(false);
      });
  }

  return (
    <Panel className="p-5">
      <form className="flex flex-col gap-6" noValidate onSubmit={submit}>
        <Fieldset
          description="When to alert about this feed's stream."
          legend="Self-check"
        >
          <div className="sm:col-span-2">
            <Switch
              checked={watching}
              description="Off: this feed isn't monitored."
              label="Watch this feed"
              onChange={(next) => {
                setWatching(next);
                setSaved(null);
              }}
            />
          </div>

          {POLICY_FIELDS.map((field) => (
            <Field
              hint={field.hint}
              htmlFor={`policy-${field.key}`}
              key={field.key}
              label={field.label}
            >
              <NumberInput
                disabled={!watching}
                id={`policy-${field.key}`}
                max={field.max}
                min={field.min}
                onChange={(event) => {
                  const value = event.target.value;
                  setDraft((currentDraft) =>
                    currentDraft
                      ? { ...currentDraft, [field.key]: value }
                      : currentDraft,
                  );
                  setSaved(null);
                }}
                step={field.step}
                unit={field.unit}
                value={draft[field.key]}
              />
            </Field>
          ))}

          <Field hint="Read-only." label="Bitrate drift">
            <p className="font-mono text-xs text-ink-3">
              {`${String(current.bitrateTolerancePercent)} %`}
            </p>
          </Field>
        </Fieldset>

        {silenceTooLong ? (
          <Notice tone="warning">
            Silence tolerated cannot be longer than the sample — silence beyond
            the sample could never be observed.
          </Notice>
        ) : null}
        {error ? <Notice tone="danger">{error}</Notice> : null}

        <FormActions note={saved ?? undefined}>
          <Button disabled={!dirty || busy || silenceTooLong} type="submit">
            {busy ? "Saving…" : "Save tolerances"}
          </Button>
          {dirty ? (
            <Button
              onClick={() => {
                setDraft(policyDraftOf(current));
                setWatching(current.enabled);
                setError(null);
              }}
              variant="ghost"
            >
              Discard
            </Button>
          ) : null}
        </FormActions>
      </form>
    </Panel>
  );
}

function DangerZone({ feed }: { feed: Feed }) {
  const navigate = useNavigate();
  const { busy, outcome, run } = useFeedActions(feed, () => {
    void navigate("/feeds");
  });

  return (
    <Panel className="border-[color-mix(in_oklch,var(--err)_30%,var(--border))] p-5">
      <h2 className="text-sm font-semibold text-ink">Delete this feed</h2>
      <p className="mt-1 max-w-2xl text-xs leading-5 text-ink-3">
        Deletes the feed, its deliveries and its play history. Your library,
        playlists and schedules aren't affected.
      </p>
      <div className="mt-4">
        <InlineConfirm
          busy={busy === "delete"}
          confirmLabel="Delete feed"
          onConfirm={() => {
            run("delete");
          }}
          question={`Delete ${feed.name}?`}
          trigger={(open) => (
            <Button
              className="text-err hover:border-err"
              onClick={open}
              size="small"
              variant="outline"
            >
              <Trash2 aria-hidden="true" />
              Delete feed
            </Button>
          )}
        >
          This cannot be undone.
        </InlineConfirm>
      </div>
      {outcome ? (
        <p
          className={
            outcome.tone === "err"
              ? "mt-3 text-xs text-err"
              : "mt-3 text-xs text-warn"
          }
        >
          {outcome.text}
        </p>
      ) : null}
    </Panel>
  );
}
